import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { UIAction } from 'app/slices/UI'
import RichTextEditor from 'components/RichTextEditor'
import SelectItem from 'components/Select'
import ShowImages from 'components/ShowImages'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'

interface FormInput {
  eventId: number
  img: string
  summary: string
  content: string
  related: string
}
export default function FeedbackRegister() {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const events = useAppSelector((s) => s.Event.list)
  const schema = yup.object({
    eventId: yup.string().required('イベントを選択してください。'),
    img: yup.string().required('画像を設定して下さい。'),
    summary: yup.string().required('まとめを入力してください。'),
    content: yup.string().required('内容を入力してください。'),
  })
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      content: '<p></p>',
      related: '',
    },
  })
  const [state, setState] = useState({
    showSelectThumbnail: false,
  })
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: 'ご利用者様の声管理',
          url: '/feedback',
        },
        {
          text: '新規登録',
          url: '/feedback/register',
        },
      ])
    )
  }, [dispatch])
  const onSubmitForm: SubmitHandler<FormInput> = async (data) => {
    try {
      setLoading(true)
      await axiosClient.post('/api/feedback/', data)

      enqueueSnackbar('新規作成が完了しました。', {
        variant: 'success',
      })
      navigate('/feedback')
    } catch (error) {
      enqueueSnackbar('エラーが発生しました。', {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Grid container spacing={1} mt={2}>
      {state.showSelectThumbnail && (
        <ShowImages
          open={state.showSelectThumbnail}
          handleClose={() => setState({ ...state, showSelectThumbnail: false })}
          handleSelect={(val) => {
            setValue('img', val, {
              shouldValidate: true,
            })
            setState({ ...state, showSelectThumbnail: false })
          }}
        />
      )}
      <Grid item xs={12}>
        <Typography
          variant="h5"
          component="h2"
          fontWeight="bold"
          sx={{ borderBottom: 2 }}
        >
          新規登録
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Container
          disableGutters
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            margin: '0',
            padding: '0 !important',
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            position: 'relative',
          }}
          onSubmit={handleSubmit(onSubmitForm)}
        >
          <Box
            border="1px solid"
            borderColor={errors.img ? 'red' : '#ccc'}
            borderRadius="0.2rem"
            width="100%"
            maxWidth="380px"
            p={1}
            my={2}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography>サムネイル</Typography>
              <Button
                onClick={() =>
                  setState({ ...state, showSelectThumbnail: true })
                }
                variant="outlined"
                size="small"
              >
                {getValues('img') ? '再選択する' : '選択する'}
              </Button>
            </Box>
            {!!getValues('img') && (
              <Box
                component="img"
                width="100%"
                src={getValues('img')}
                mt={2}
              ></Box>
            )}
          </Box>
          <SelectItem
            items={events}
            value={getValues('eventId')}
            label="イベント名"
            helperText={errors.eventId?.message}
            handleSelect={(val) => {
              setValue('eventId', val, {
                shouldValidate: true,
              })
            }}
          />
          <TextField
            required
            label="概要"
            margin="dense"
            fullWidth
            size="small"
            variant="outlined"
            {...register('summary')}
            error={'summary' in errors}
            helperText={errors.summary?.message}
          />
          <TextField
            label="関連ページ"
            margin="dense"
            fullWidth
            size="small"
            variant="outlined"
            {...register('related')}
            error={'related' in errors}
            helperText={errors.related?.message}
          />

          <Typography>内容</Typography>
          <RichTextEditor
            value={getValues('content')}
            setValue={(val) => setValue('content', val)}
          />

          <Stack mt={4}>
            <LoadingButton
              loading={loading}
              disabled={Object.keys(errors).length > 0}
              size="small"
              color="secondary"
              variant="contained"
              sx={{
                borderRadius: 2,
                width: 200,
                fontWeight: 600,
                fontSize: '1rem',
              }}
              type="submit"
            >
              登録
            </LoadingButton>
          </Stack>
        </Container>
      </Grid>
    </Grid>
  )
}
